import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ImageIcon, ChevronDown } from "lucide-react";
import { adminFetch, STATUS_LABELS, STATUS_COLORS } from "../adminApi";
import { API } from "@/lib/site";
import { inputCls, labelCls } from "../ui";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "–";
  }
}

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState(null);
  const [status, setStatus] = useState("alle");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("desc");
  const [detail, setDetail] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status !== "alle") params.set("status", status);
    if (q.trim()) params.set("q", q.trim());
    params.set("sort", sort);
    try {
      const d = await adminFetch(`/admin/inquiries?${params.toString()}`);
      setInquiries(d.inquiries);
    } catch (e) {
      toast.error(e.message);
      setInquiries([]);
    }
  }, [status, q, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    if (detail && detail.id === id) {
      setDetail(null);
      return;
    }
    try {
      const d = await adminFetch(`/admin/inquiries/${id}`);
      setDetail(d);
      setNotesDraft(d.notes || "");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const setStatusFor = async (id, s) => {
    try {
      const d = await adminFetch(`/admin/inquiries/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: s }),
      });
      setDetail(d.inquiry);
      await load();
      toast.success(`Status geändert: ${STATUS_LABELS[s] || s}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveNotes = async () => {
    if (!detail) return;
    try {
      const d = await adminFetch(`/admin/inquiries/${detail.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: notesDraft }),
      });
      setDetail(d.inquiry);
      await load();
      toast.success("Interne Notiz gespeichert.");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          data-testid="admin-inquiries-search"
          className={`${inputCls} flex-1`}
          placeholder="Suchen: Name, Ort, Paket …"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select data-testid="admin-inquiries-sort" className={inputCls} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="desc">Neueste zuerst</option>
          <option value="asc">Älteste zuerst</option>
        </select>
      </div>

      <div className="flex flex-wrap gap-2">
        {["alle", "neu", "kontaktiert", "termin", "erledigt", "storniert"].map((s) => (
          <button
            key={s}
            data-testid={`admin-inquiries-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`h-9 rounded-full px-4 text-sm font-semibold transition-colors ${
              status === s
                ? "bg-blue-600 text-white"
                : "border border-zinc-800 text-zinc-300 hover:border-zinc-600"
            }`}
          >
            {s === "alle" ? "Alle" : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {inquiries === null ? (
        <p className="text-sm text-zinc-400">Lade Anfragen …</p>
      ) : inquiries.length === 0 ? (
        <p data-testid="admin-inquiries-empty" className="text-sm text-zinc-400">
          Keine Anfragen gefunden.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {inquiries.map((inq) => (
            <div key={inq.id} className="rounded-2xl border border-[#27272A] bg-[#121215]">
              <button
                data-testid={`admin-inquiry-row-${inq.id}`}
                onClick={() => openDetail(inq.id)}
                className="w-full flex items-center justify-between gap-3 p-5 text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-100 truncate">
                    {inq.name} · <span className="text-zinc-300 font-normal">{inq.package}</span>
                  </p>
                  <p className="mt-1 text-xs text-zinc-400 truncate">
                    {fmtDate(inq.created_at)} · {inq.zip_city} · {inq.vehicle}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {inq.status === "neu" && (
                    <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" aria-label="Neu" />
                  )}
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${STATUS_COLORS[inq.status] || STATUS_COLORS.neu}`}>
                    {STATUS_LABELS[inq.status] || inq.status}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${detail?.id === inq.id ? "rotate-180" : ""}`} />
                </div>
              </button>

              {detail?.id === inq.id && (
                <div data-testid="admin-inquiry-detail" className="border-t border-white/5 p-5 flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {[
                      ["Datum", fmtDate(detail.created_at)],
                      ["Name", detail.name],
                      ["Kontaktweg", STATUS_LABELS[detail.contact_method] || detail.contact_method || "–"],
                      ["Telefon", detail.phone || "–"],
                      ["E-Mail", detail.email || "–"],
                      ["PLZ / Ort", detail.zip_city],
                      ["Fahrzeug", detail.vehicle],
                      ["Gewähltes Paket", detail.package],
                      ["Zusatzleistungen", detail.addons?.length ? detail.addons.join(", ") : "–"],
                      ["Richtpreis (Rechner)", detail.estimated_price || "–"],
                      ["Wunschtermin", detail.desired_date || "–"],
                    ].map(([label, value]) => (
                      <p key={label} className="text-zinc-300">
                        <span className="text-zinc-500">{label}:</span> <span className="text-zinc-100 break-all">{value}</span>
                      </p>
                    ))}
                  </div>

                  {detail.message ? (
                    <div>
                      <p className={labelCls}>Nachricht</p>
                      <p className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed">{detail.message}</p>
                    </div>
                  ) : null}

                  <div>
                    <p className={`${labelCls} flex items-center gap-1.5`}>
                      <ImageIcon className="h-3.5 w-3.5" /> Fotos ({detail.photos?.length || 0})
                    </p>
                    {detail.photos?.length ? (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {detail.photos.map((p, i) => (
                          <img
                            key={i}
                            data-testid={`admin-inquiry-photo-${i}`}
                            src={`${API}/admin/inquiries/${detail.id}/photos/${i}`}
                            alt={`Kundenfoto ${i + 1}`}
                            className="h-24 w-24 rounded-lg object-cover border border-zinc-800"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-400">Keine Fotos hochgeladen.</p>
                    )}
                    <p className="mt-2 text-[11px] text-zinc-500">
                      Kundenfotos sind geschützt und nur hier im Adminbereich sichtbar.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Status</label>
                      <select
                        data-testid="admin-inquiry-status-select"
                        className={inputCls}
                        value={detail.status}
                        onChange={(e) => setStatusFor(detail.id, e.target.value)}
                      >
                        {["neu", "kontaktiert", "termin", "erledigt", "storniert"].map((s) => (
                          <option key={s} value={s} className="bg-zinc-900">
                            {STATUS_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Interne Notizen</label>
                      <textarea
                        data-testid="admin-inquiry-notes"
                        className={`${inputCls} min-h-[80px] py-2.5`}
                        placeholder="Interne Notizen (z. B. Rückruf erledigt, Absprachen …)"
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        maxLength={4000}
                      />
                      <button
                        data-testid="admin-inquiry-notes-save"
                        onClick={saveNotes}
                        className="mt-2 h-9 rounded-full bg-blue-600 px-5 text-xs font-semibold text-white hover:bg-blue-500 transition-colors"
                      >
                        Notiz speichern
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
